/** Premium-preview collection navigation. No cart, catalog or analytics writer.
 * Contract: custom-code-padelusa/features/collection-navigation-speed.md. */
const TTL = 30_000;
const LIMIT = 6;
const supportedCollectionTemplate = value => ['collection', 'collection.cll-detail', 'collection.racket-detail'].includes(value);

/** Only real same-origin collection pages; leave other routes and hashes native. */
export function collectionURL(href, base) {
  try {
    const url = new URL(href, base);
    if (url.origin !== new URL(base).origin || url.hash ||
      !/^\/(?:[a-z]{2}(?:-[a-z]{2})?\/)?collections\/[^/]+\/?$/i.test(url.pathname)) return null;
    // Keep an explicitly selected preview comparison when browsing other collections.
    if (new URL(base).searchParams.get('pusa_sale_layout') === 'image' &&
        !url.searchParams.has('pusa_sale_layout')) url.searchParams.set('pusa_sale_layout', 'image');
    const current = new URL(base);
    if (['cll-detail', 'racket-detail'].includes(current.searchParams.get('view'))) {
      url.searchParams.set('view', current.searchParams.get('view'));
      const preview = current.searchParams.get('preview_theme_id');
      if (preview) url.searchParams.set('preview_theme_id', preview);
    }
    url.searchParams.sort();
    return url;
  } catch { return null; }
}

/** Session-memory only, short-lived server HTML; pending requests are deduplicated. */
export class CollectionPageCache {
  entries = new Map();
  pending = new Map();
  constructor(load, now = Date.now, ttl = TTL, limit = LIMIT) {
    this.load = load; this.now = now; this.ttl = ttl; this.limit = limit;
  }
  set(key, value) {
    this.entries.delete(key);
    this.entries.set(key, { value, expires: this.now() + this.ttl });
    while (this.entries.size > this.limit) this.entries.delete(this.entries.keys().next().value);
  }
  has(key) {
    const entry = this.entries.get(key);
    if (entry && entry.expires > this.now()) return true;
    this.entries.delete(key);
    return false;
  }
  get(key) {
    if (this.has(key)) {
      const entry = this.entries.get(key);
      this.entries.delete(key); this.entries.set(key, entry);
      return Promise.resolve(entry.value);
    }
    if (this.pending.has(key)) return this.pending.get(key);
    const request = Promise.resolve().then(() => this.load(key)).then(value => {
      this.set(key, value); return value;
    }).finally(() => this.pending.delete(key));
    this.pending.set(key, request);
    return request;
  }
}

/** Discard responses superseded by another tag, facet edit or Back/Forward. */
export class NavigationGeneration {
  value = 0;
  next() { return ++this.value; }
  current(value) { return value === this.value; }
}

async function boot() {
  const main = document.getElementById('MainContent');
  if (!document.body.classList.contains('pusa-premium-preview') ||
      !supportedCollectionTemplate(main?.dataset.template) || !main.querySelector('collection-filter-chips') ||
      window.Shopify?.designMode) return;
  const [{ morph, MORPH_OPTIONS }, { sectionRenderer }] = await Promise.all([
    import('@theme/morph'), import('@theme/section-renderer'),
  ]);
  const generation = new NavigationGeneration();
  const status = document.createElement('div');
  status.className = 'visually-hidden'; status.setAttribute('role', 'status');
  status.setAttribute('aria-live', 'polite'); main.after(status);
  const root = document.documentElement;
  root.setAttribute('data-collection-navigation', '');
  let renderedURL = location.href;
  let warming = false;
  const metadataSelector = 'link[rel="canonical"], link[rel="alternate"][hreflang], meta[name="description"], meta[property^="og:"], meta[name^="twitter:"], script[type="application/ld+json"]';
  const sectionIDs = () => [...main.children].map(node => node.id);
  const serializeContent = content => {
    // innerHTML excludes live declarative shadow roots. Restore their template
    // markup in the cached copy so controls can hydrate when a route remounts.
    const sourceNodes = [...content.querySelectorAll('*')];
    if (!sourceNodes.some(node => node.shadowRoot)) return content.innerHTML;
    const copy = content.cloneNode(true);
    const copyNodes = [...copy.querySelectorAll('*')];
    sourceNodes.forEach((node, index) => {
      if (!node.shadowRoot) return;
      const template = document.createElement('template');
      template.setAttribute('shadowrootmode', 'open');
      template.innerHTML = node.shadowRoot.innerHTML;
      copyNodes[index].prepend(template);
    });
    return copy.innerHTML;
  };
  const readPage = doc => {
    const content = doc.getElementById('MainContent');
    if (content?.dataset.template !== main.dataset.template || !content.querySelector('#ResultsList') ||
        JSON.stringify([...content.children].map(node => node.id)) !== JSON.stringify(sectionIDs())) {
      throw new Error('Collection template requires a full navigation');
    }
    return { html: serializeContent(content), title: doc.title,
      metadata: [...doc.head.querySelectorAll(metadataSelector)].map(node => node.outerHTML) };
  };
  const cache = new CollectionPageCache(async href => {
    const abort = new AbortController();
    const timeout = setTimeout(() => abort.abort(), 8_000);
    try {
      const response = await fetch(href, { signal: abort.signal, credentials: 'same-origin' });
      if (!response.ok || !collectionURL(response.url, href)) throw new Error('Collection fetch failed');
      return readPage(new DOMParser().parseFromString(await response.text(), 'text/html'));
    } finally { clearTimeout(timeout); }
  });
  // Shopify consumes the preview parameter on arrival. Give all Detail cache keys
  // the same explicit theme identity, avoiding duplicate initial/history entries.
  const detailTheme = main.querySelector('results-list[data-cll-view="cll-detail"]')?.dataset.cllTheme;
  const pageURL = href => {
    const url = collectionURL(href, location.href);
    if (url && detailTheme) {
      url.searchParams.set('preview_theme_id', detailTheme);
      url.searchParams.sort();
    }
    return url;
  };
  const key = href => pageURL(href)?.href;
  cache.set(key(location.href), readPage(document));
  const saveScroll = () => history.replaceState({ ...history.state, pusaCollectionScroll: scrollY }, '', location.href);
  saveScroll();
  const canWarm = () => !navigator.connection?.saveData && !/^(slow-)?2g$/.test(navigator.connection?.effectiveType || '');
  const chipFor = event => event.target instanceof Element &&
    event.target.closest('collection-filter-chips:not([mode="filter"]) a.cfc-chip');
  const warmIntent = event => {
    if (!canWarm() || cache.pending.size >= 2) return;
    const chip = chipFor(event);
    const href = chip && key(chip.href);
    if (href && href !== key(location.href)) cache.get(href).catch(() => {});
  };
  // Warm at most five visible siblings, serially. No catalog-wide speculative crawl.
  const warmSiblings = () => {
    if (warming || !canWarm()) return;
    const hrefs = [...new Set([...main.querySelectorAll('collection-filter-chips:not([mode="filter"]) a.cfc-chip')]
      .map(chip => key(chip.href)).filter(href => href && href !== key(location.href)))].slice(0, 5);
    warming = true;
    const idle = window.requestIdleCallback || (callback => setTimeout(callback, 0));
    idle(async () => {
      for (const href of hrefs) { try { await cache.get(href); } catch { /* Click retains native fallback. */ } }
      warming = false;
    });
  };
  const clearBusy = () => {
    main.removeAttribute('aria-busy');
    main.querySelectorAll('.cfc-chip--pending').forEach(chip => chip.classList.remove('cfc-chip--pending'));
  };
  const cancelSectionMorphs = () => sectionIDs().forEach(id => sectionRenderer.cancelRendering(id.replace(/^shopify-section-/, '')));
  async function navigate(url, chip, pop = false) {
    url = pageURL(url.href) || url;
    const token = generation.next();
    const started = performance.now();
    const reused = cache.has(url.href);
    const oldY = scrollY;
    const barTop = main.querySelector('collection-filter-chips')?.getBoundingClientRect().top;
    const label = chip?.textContent.trim();
    const destinationScroll = history.state?.pusaCollectionScroll || 0;
    if (!pop) saveScroll();
    cancelSectionMorphs();
    clearBusy(); main.setAttribute('aria-busy', 'true');
    chip?.classList.add('cfc-chip--pending');
    status.textContent = label ? `Loading ${label}…` : 'Loading collection…';
    try {
      const page = await cache.get(url.href);
      if (!generation.current(token)) return;
      const next = main.cloneNode(false);
      next.innerHTML = page.html;
      // Native paginated-list caches belong to their URL. Remount just that section.
      const oldResultsSection = main.querySelector('results-list')?.closest('.shopify-section');
      const newResultsSection = next.querySelector('results-list')?.closest('.shopify-section');
      if (oldResultsSection && newResultsSection) {
        const view = main.querySelector('.product-grid')?.getAttribute('product-grid-view');
        if (view) next.querySelector('.product-grid')?.setAttribute('product-grid-view', view);
        oldResultsSection.dataset.collectionRoute = renderedURL;
        newResultsSection.dataset.collectionRoute = url.href;
      }
      if (!pop) history.pushState({ pusaCollectionScroll: oldY }, '', url.href);
      renderedURL = location.href;
      morph(main, next, { ...MORPH_OPTIONS,
        getNodeKey: node => node instanceof Element && node.dataset.collectionRoute
          ? node.id + node.dataset.collectionRoute : undefined,
      });
      document.title = page.title;
      document.head.querySelectorAll(metadataSelector).forEach(node => node.remove());
      page.metadata.forEach(html => {
        const template = document.createElement('template'); template.innerHTML = html;
        document.head.append(template.content);
      });
      document.dispatchEvent(new CustomEvent('collection:navigate', { detail: { url: renderedURL } }));
      // Let native components/observers hydrate before returning focus and anchoring scroll.
      await new Promise(resolve => requestAnimationFrame(resolve));
      if (!generation.current(token)) return;
      const newBarTop = main.querySelector('collection-filter-chips')?.getBoundingClientRect().top;
      scrollTo({ top: pop ? destinationScroll : oldY === 0 ? 0 : Math.max(0, oldY + (newBarTop - barTop || 0)), behavior: 'instant' });
      if (chip) {
        const selected = [...main.querySelectorAll('a.cfc-chip')].find(link => key(link.href) === url.href);
        selected?.focus({ preventScroll: true });
      }
      clearBusy();
      status.textContent = `${main.querySelector('h1')?.textContent.trim() || 'Collection'} loaded.`;
      main.dataset.collectionNavigationMs = String(Math.round(performance.now() - started));
      main.dataset.collectionNavigationCache = reused ? 'hit' : 'miss';
      warmSiblings();
    } catch {
      if (generation.current(token)) location.assign(url.href);
    }
  }
  document.addEventListener('click', event => {
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const chip = chipFor(event);
    if (!chip || chip.target || chip.hasAttribute('download')) return;
    const url = collectionURL(chip.href, location.href);
    if (!url) return;
    event.preventDefault();
    if (url.href === key(renderedURL)) {
      generation.next(); clearBusy(); status.textContent = ''; return;
    }
    navigate(url, chip);
  });
  document.addEventListener('pointerover', warmIntent);
  document.addEventListener('focusin', warmIntent);
  document.addEventListener('touchstart', warmIntent, { passive: true });
  document.addEventListener('filter:update', () => { generation.next(); clearBusy(); status.textContent = ''; });
  window.addEventListener('popstate', () => {
    const url = collectionURL(location.href, renderedURL);
    if (url) navigate(url, null, true); else location.reload();
  });
  warmSiblings();
}

if (typeof document !== 'undefined') boot().catch(() => document.documentElement.removeAttribute('data-collection-navigation'));
